import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  TextField,
  Alert,
  Divider,
  Stack,
  Chip
} from '@mui/material';
import {
  Add,
  Remove,
  People,
  CreditCard
} from '@mui/icons-material';

interface SeatManagementDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (newSeatCount: number) => Promise<void>;
  currentSeats: number;
  usedSeats: number;
  mode: 'add' | 'remove';
  loading?: boolean;
  pricePerSeat?: number;
}

const SeatManagementDialog: React.FC<SeatManagementDialogProps> = ({
  open,
  onClose,
  onConfirm,
  currentSeats,
  usedSeats,
  mode,
  loading = false,
  pricePerSeat = 15
}) => {
  const [seatCount, setSeatCount] = useState(currentSeats);
  const [inputValue, setInputValue] = useState(currentSeats.toString());

  // Reset state when dialog opens or current seats change
  useEffect(() => {
    if (open) {
      setSeatCount(currentSeats);
      setInputValue(currentSeats.toString());
    }
  }, [open, currentSeats]);

  const handleIncrement = () => {
    const newCount = seatCount + 1;
    setSeatCount(newCount);
    setInputValue(newCount.toString());
  };

  const handleDecrement = () => {
    const newCount = Math.max(usedSeats, seatCount - 1);
    setSeatCount(newCount);
    setInputValue(newCount.toString());
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInputValue(value);
    
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= usedSeats) {
      setSeatCount(numValue);
    }
  };

  const handleInputBlur = () => {
    const numValue = parseInt(inputValue, 10);
    if (isNaN(numValue) || numValue < usedSeats) {
      setSeatCount(usedSeats);
      setInputValue(usedSeats.toString());
    } else {
      setSeatCount(numValue);
      setInputValue(numValue.toString());
    }
  };

  const handleConfirm = async () => {
    await onConfirm(seatCount);
  };

  const seatDifference = seatCount - currentSeats;
  const isAdding = seatDifference > 0;
  const isRemoving = seatDifference < 0;
  const isNoChange = seatDifference === 0;

  const getDialogTitle = () => {
    return 'Manage Seats';
  };

  const getActionButtonText = () => {
    if (loading) return 'Processing...';
    if (isAdding) return `Add ${seatDifference} Seat${seatDifference !== 1 ? 's' : ''}`;
    if (isRemoving) return `Remove ${Math.abs(seatDifference)} Seat${Math.abs(seatDifference) !== 1 ? 's' : ''}`;
    return 'Update Seats';
  };

  const getActionButtonColor = () => {
    if (isAdding) return 'primary';
    if (isRemoving) return 'warning';
    return 'primary';
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '700px',
          maxHeight: '700px'
        }
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <People color="primary" />
          <Typography variant="h6">{getDialogTitle()}</Typography>
        </Stack>
      </DialogTitle>
      
      <DialogContent sx={{ overflowY: 'auto' }}>
        {/* Current Status */}
        <Box mb={3}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Current subscription status:
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip 
              label={`${currentSeats} seats purchased`} 
              color="primary" 
              variant="outlined"
            />
            <Chip 
              label={`${usedSeats} seats used`} 
              color={usedSeats > currentSeats ? "error" : "success"}
              variant="outlined"
            />
          </Stack>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Seat Management Controls */}
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            Adjust Seat Count
          </Typography>
          
          <Stack direction="row" alignItems="center" spacing={2} justifyContent="center" sx={{ my: 3 }}>
            <IconButton 
              onClick={handleDecrement}
              disabled={seatCount <= usedSeats}
              color="primary"
              size="large"
              sx={{ 
                border: '2px solid',
                borderColor: 'primary.main',
                '&:disabled': {
                  borderColor: 'grey.300',
                  color: 'grey.400'
                }
              }}
            >
              <Remove />
            </IconButton>
            
            <TextField
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              type="number"
              inputProps={{ 
                min: usedSeats,
                style: { textAlign: 'center', fontSize: '1.5rem', fontWeight: 'bold' }
              }}
              sx={{ 
                width: 120,
                '& .MuiOutlinedInput-root': {
                  fontSize: '1.5rem',
                  fontWeight: 'bold'
                }
              }}
            />
            
            <IconButton 
              onClick={handleIncrement}
              color="primary"
              size="large"
              sx={{ 
                border: '2px solid',
                borderColor: 'primary.main'
              }}
            >
              <Add />
            </IconButton>
          </Stack>

          {seatCount < usedSeats && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Cannot reduce seats below the number currently in use ({usedSeats} seats).
            </Alert>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Preview Changes */}
        <Box>
          <Typography variant="h6" gutterBottom>
            <CreditCard sx={{ mr: 1, verticalAlign: 'middle' }} />
            Billing Preview
          </Typography>
          
          {isNoChange ? (
            <Alert severity="info">
              <Typography variant="body2">
                No changes to your current subscription.
              </Typography>
            </Alert>
          ) : (
            <Box>
              <Stack spacing={2}>
                <Box p={2} bgcolor="grey.50" borderRadius={1}>
                  <Typography variant="body2" color="text.secondary">
                    Current: {currentSeats} seats × ${pricePerSeat} = ${(currentSeats * pricePerSeat).toFixed(2)}/month
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    New: {seatCount} seats × ${pricePerSeat} = ${(seatCount * pricePerSeat).toFixed(2)}/month
                  </Typography>
                </Box>
                
                <Box p={2} bgcolor={isAdding ? "success.50" : "warning.50"} borderRadius={1}>
                  <Typography variant="h6" color={isAdding ? "success.main" : "warning.main"}>
                    {isAdding ? '+' : ''}${(seatDifference * pricePerSeat).toFixed(2)}/month
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {isAdding ? 'Additional cost' : 'Cost reduction'} per month
                  </Typography>
                </Box>

                {isAdding && (
                  <Alert severity="info">
                    <Typography variant="body2">
                      <strong>Prorated billing:</strong> You'll be charged immediately for the additional seats for the current billing period.
                    </Typography>
                  </Alert>
                )}

                {isRemoving && (
                  <Alert severity="warning">
                    <Typography variant="body2">
                      <strong>Next billing cycle:</strong> Seat reductions take effect at the next billing cycle. You'll continue to pay for the current number of seats until then.
                    </Typography>
                  </Alert>
                )}
              </Stack>
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={getActionButtonColor()}
          disabled={loading || isNoChange || seatCount < usedSeats}
        >
          {getActionButtonText()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SeatManagementDialog;
